import { useEffect, useRef } from 'react';
import { Post } from './Post';
import { useUserProfile } from '@/context/UserProfileContext';

type UserProfileMediaProps = {
  posts: any[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
};

export function UserProfileMedia({ posts, loading = false, onLoadMore, hasMore = false }: UserProfileMediaProps) {
  // Get current user profile
  const { profile } = useUserProfile();

  // Only show posts with media
  const postsWithMedia = posts.filter((post) => post.media && post.media.length > 0);

  // Intersection Observer for infinite scrolling
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || loading || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  // Handle post deletion (placeholder function)
  const handleDeletePost = (postId: number) => {
    console.log('Delete post:', postId);
    // Implement actual deletion if needed
  };

  return (
    <div className="space-y-4 max-w-xl mx-auto text-left text-foreground">
      {postsWithMedia.map((post) => (
        <Post
          key={post.id}
          id={post.id}
          user={{
            id: post.user?.id || profile?.id || 0,
            firstName: post.user?.firstName || profile?.firstName || '',
            lastName: post.user?.lastName || profile?.lastName || '',
            imageUrl: post.user?.imageUrl || profile?.imageUrl || '',
            username: post.user?.username || profile?.username || ''
          }}
          media={post.media || []}
          likes={post.likes || 0}
          isLiked={post.isLiked || false}
          course={post.course}
          caption={post.caption || ''}
          comments={post.comments || 0}
          audience={post.audience || 0}
          profileId={profile?.id || 0}
          onDelete={handleDeletePost}
          createdAt={post.createdAt}
          className="mb-2"
        />
      ))}

      {/* Loading indicator and observer target */}
      <div ref={observerTarget} className="py-4 flex justify-center">
        {loading && (
          <div className="animate-pulse text-center text-muted-foreground">Loading more posts...</div>
        )}
        {!loading && postsWithMedia.length === 0 && (
          <div className="text-center text-muted-foreground">No posts yet</div>
        )}
      </div>
    </div>
  );
}

